import React, { useCallback, useEffect, useState } from "react";
import { departmentsUser, downloadReportExcel, getIncidentReports } from "../services/api";
import { subscribeToRealtimeEvent } from "../services/socket";
import { useBrand } from "../context/BrandContext";

const STATUS_OPTIONS = [
  "OVR Submitted",
  "Sent for User Comment",
  "User Comment Received",
  "Investigation Completed",
  "Sent for HOD Review",
  "HOD Comment Submitted",
  "Final Review",
  "Closed"
];

const formatAge = (incident) => {
  const years = incident.ageYears ?? incident.age ?? 0;
  const months = incident.ageMonths ?? 0;
  if (!years && !months) return "";
  return `${years || 0} Years ${months || 0} Months`;
};

const downloadBlob = (response, fallback) => {
  const blob = new Blob([response.data], {
    type: response.headers?.["content-type"] || "application/octet-stream"
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fallback;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const Reports = () => {
  const { brand } = useBrand();
  const [filters, setFilters] = useState({
    refNo: "",
    status: "",
    department: "",
    dateFrom: "",
    dateTo: ""
  });
  const [departments, setDepartments] = useState([]);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtersCollapsed, setFiltersCollapsed] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const totalPages = Math.max(1, Math.ceil(incidents.length / limit));
  const pageStart = incidents.length === 0 ? 0 : (page - 1) * limit + 1;
  const pageEnd = Math.min(page * limit, incidents.length);
  const pagedIncidents = incidents.slice((page - 1) * limit, page * limit);
  const getPageNumbers = () => {
    const pages = [];
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    const end = Math.min(totalPages, start + 4);

    for (let current = start; current <= end; current += 1) {
      pages.push(current);
    }

    return pages;
  };

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await getIncidentReports(filters);
      setIncidents(data.incidents || []);
      setPage(1);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    departmentsUser().then((res) => setDepartments(res.data || []));
    getIncidentReports({}).then(({ data }) => setIncidents(data.incidents || []));
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToRealtimeEvent("incident:changed", () => {
      loadReports();
    });

    return () => unsubscribe?.();
  }, [loadReports]);

  const updateFilter = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const exportExcel = async () => {
    const response = await downloadReportExcel(filters);
    downloadBlob(response, "Incident_Report.xlsx");
  };

  return (
    <div className="report-page">
      <div className="report-header">
        <div>
          <h3>OVR Registry</h3>
          <p>Search, review, and export incident registry records.</p>
        </div>
        <button
          type="button"
          className="report-filter-toggle"
          onClick={() => setFiltersCollapsed((value) => !value)}
        >
          <i className="bi bi-funnel" aria-hidden="true"></i>
          <span>{filtersCollapsed ? "Show Filters" : "Hide Filters"}</span>
          <i className={`bi ${filtersCollapsed ? "bi-chevron-down" : "bi-chevron-up"}`} aria-hidden="true"></i>
        </button>
      </div>

      {!filtersCollapsed && (
        <div className="report-filter-panel">
          <div className="report-filter-grid">
            <label>
              <span>Ref No</span>
              <input
                className="form-control"
                placeholder={brand?.refNoPrefix ? `${brand.refNoPrefix}0000001` : 'AZG/RMC/OVR/0000001'}
                value={filters.refNo}
                onChange={(e) => updateFilter("refNo", e.target.value)}
              />
            </label>
            <label>
              <span>Status</span>
              <select
                className="form-select"
                value={filters.status}
                onChange={(e) => updateFilter("status", e.target.value)}
              >
                <option value="">All Statuses</option>
                {STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>{status}</option>
                ))}
              </select>
            </label>
            <label>
              <span>Department</span>
              <select
                className="form-select"
                value={filters.department}
                onChange={(e) => updateFilter("department", e.target.value)}
              >
                <option value="">All Departments</option>
                {departments.map((dept) => (
                  <option key={dept._id} value={dept._id}>
                    {dept.departmentName}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>From</span>
              <input
                type="date"
                className="form-control"
                value={filters.dateFrom}
                onChange={(e) => updateFilter("dateFrom", e.target.value)}
              />
            </label>
            <label>
              <span>To</span>
              <input
                type="date"
                className="form-control"
                value={filters.dateTo}
                onChange={(e) => updateFilter("dateTo", e.target.value)}
              />
            </label>
          </div>

          <div className="report-filter-actions">
            <button className="report-search-btn" onClick={loadReports} disabled={loading}>
              <i className="bi bi-search" aria-hidden="true"></i>
              <span>{loading ? "Searching" : "Search"}</span>
            </button>
          </div>
        </div>
      )}

      <div className="dashboard-list-panel report-grid-panel">
        <div className="dashboard-list-header">
          <div>
            <div className="report-grid-title">Registry Records</div>
            <div className="list-count">
              Showing {pageStart}-{pageEnd} of {incidents.length} records
            </div>
          </div>
          <div className="dashboard-table-tools">
            <label>
              Rows
              <select
                value={limit}
                onChange={(event) => {
                  setLimit(Number(event.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50].map((size) => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="report-export-btn"
              onClick={exportExcel}
              title="Export Excel"
            >
              <i className="bi bi-file-earmark-excel" aria-hidden="true"></i>
              <span>Excel</span>
            </button>
          </div>
        </div>

        <div className="modern-table-wrap">
          <table className="table modern-dashboard-table report-table">
            <thead>
              <tr>
                <th>Sl No</th>
                <th>Ref No</th>
                <th>Date</th>
                <th>EMR No/Name</th>
                <th>Department</th>
                <th>Age</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {pagedIncidents.length ? pagedIncidents.map((incident, index) => (
                <tr key={incident._id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>{incident.refNo}</td>
                  <td>{incident.date ? new Date(incident.date).toLocaleDateString() : ""}</td>
                  <td>{incident.emrNoName || ""}</td>
                  <td>{incident.department?.departmentName || ""}</td>
                  <td>{formatAge(incident)}</td>
                  <td>
                    <span className="report-status-pill">{incident.currentStatus}</span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" className="text-center text-muted">
                    {loading ? "Loading..." : "No incidents found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="dashboard-pagination">
          <div className="pagination-summary">
            Showing {pageStart}-{pageEnd} of {incidents.length}
          </div>
          <div className="modern-pagination">
            <button type="button" disabled={page === 1} onClick={() => setPage(1)} title="First page">
              <i className="bi bi-chevron-double-left" aria-hidden="true"></i>
            </button>
            <button type="button" disabled={page === 1} onClick={() => setPage(page - 1)} title="Previous page">
              <i className="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            {getPageNumbers().map((pageNumber) => (
              <button
                key={pageNumber}
                type="button"
                className={pageNumber === page ? "active" : ""}
                onClick={() => setPage(pageNumber)}
              >
                {pageNumber}
              </button>
            ))}
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(page + 1)} title="Next page">
              <i className="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
            <button type="button" disabled={page >= totalPages} onClick={() => setPage(totalPages)} title="Last page">
              <i className="bi bi-chevron-double-right" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Reports;
